import { createApiKey, getKeyScopes, listApiKeys, updateApiKeyTags } from '@mesa/core/auth/api-keys';
import { auth } from '@mesa/core/auth/better-auth';
import { DEFAULT_SCOPES, type Scope } from '@mesa/core/auth/types';
import { assertKeyScopeCeiling } from '@mesa/core/auth/utils';
import { createServerFn } from '@tanstack/react-start';
import { assertScope, baseAuthMiddleware } from '../middleware/base-auth';

async function listAllApiKeys(orgId: string) {
  const allKeys = [];
  let cursor: string | undefined;

  for (let page = 0; page < 100; page += 1) {
    const result = await listApiKeys(orgId, { cursor, limit: 100 });
    allKeys.push(...result.apiKeys);

    if (!result.hasMore || !result.nextCursor) {
      break;
    }

    cursor = result.nextCursor;
  }

  return allKeys.map(({ createdAtCursor: _, ...key }) => key);
}

export const listApiKeysServerFn = createServerFn({ method: 'GET' })
  .middleware([baseAuthMiddleware])
  .inputValidator((data: { orgSlug: string }) => data)
  .handler(async ({ context: { org, member } }) => {
    assertScope(member.role, 'read');
    return listAllApiKeys(org.id);
  });

export const createApiKeyServerFn = createServerFn({ method: 'POST' })
  .middleware([baseAuthMiddleware])
  .inputValidator(
    (data: {
      orgSlug: string;
      name?: string | null;
      scopes?: Scope[];
      repoIds?: string[] | null;
      expiresInSeconds?: number | null;
      tags?: Record<string, string>;
    }) => data
  )
  .handler(async ({ data: { name, scopes, repoIds, expiresInSeconds, tags }, context: { org, member, session } }) => {
    const requestedScopes = scopes ?? (DEFAULT_SCOPES as Scope[]);
    assertKeyScopeCeiling(member.role, requestedScopes, 'create');
    const normalizedRepoIds = repoIds && repoIds.length > 0 ? repoIds : null;
    return createApiKey(
      org.id,
      session.user.id,
      name ?? null,
      requestedScopes,
      normalizedRepoIds,
      false,
      expiresInSeconds ?? null,
      tags
    );
  });

export const revokeApiKeyServerFn = createServerFn({ method: 'POST' })
  .middleware([baseAuthMiddleware])
  .inputValidator((data: { orgSlug: string; keyId: string }) => data)
  .handler(async ({ data: { keyId }, context: { member, session } }) => {
    const keyScopes = await getKeyScopes(keyId);
    assertKeyScopeCeiling(member.role, keyScopes, 'revoke');
    return auth.api.updateApiKey({ body: { keyId, userId: session.user.id, enabled: false } });
  });

export const bulkRevokeApiKeysServerFn = createServerFn({ method: 'POST' })
  .middleware([baseAuthMiddleware])
  .inputValidator((data: { orgSlug: string; keyIds: string[] }) => data)
  .handler(async ({ data: { keyIds }, context: { member, session } }) => {
    for (const keyId of keyIds) {
      const keyScopes = await getKeyScopes(keyId);
      assertKeyScopeCeiling(member.role, keyScopes, 'revoke');
    }
    await Promise.all(
      keyIds.map((keyId) => auth.api.updateApiKey({ body: { keyId, userId: session.user.id, enabled: false } }))
    );
  });

export const updateApiKeyTagsServerFn = createServerFn({ method: 'POST' })
  .middleware([baseAuthMiddleware])
  .inputValidator((data: { orgSlug: string; keyId: string; tags: Record<string, string | null> }) => data)
  .handler(async ({ data: { keyId, tags }, context: { org, member, session } }) => {
    assertScope(member.role, 'admin');
    return updateApiKeyTags(org.id, keyId, session.user.id, tags);
  });
